import { useEffect, useRef, useState } from 'react';
import Waveform from './Waveform.jsx';
import { clockLabel, peaksFromUrl } from '../lib/audio.js';

/**
 * A voice note as a thing you play, rather than a browser default player.
 *
 * The native <audio controls> widget is a different shape and colour in every
 * browser and cannot show a waveform at all.
 */
export default function AudioNote({ src, peaks: given = [], durationMs, compact = false, download = true }) {
  const audioRef = useRef(null);
  const [peaks, setPeaks] = useState(given);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(0);
  const [total, setTotal] = useState(durationMs ? durationMs / 1000 : 0);
  const [failed, setFailed] = useState(false);

  // Older clips arrived before peaks were stored, so fall back to decoding.
  useEffect(() => {
    if (given.length > 0) { setPeaks(given); return undefined; }
    let live = true;
    peaksFromUrl(src).then((computed) => { if (live) setPeaks(computed); });
    return () => { live = false; };
  }, [src, given]);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) { audio.pause(); return; }
    audio.play().catch(() => setFailed(true));
  };

  const seek = (fraction) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration)) return;
    audio.currentTime = fraction * audio.duration;
    setAt(fraction * audio.duration);
  };

  const progress = total > 0 ? Math.min(1, at / total) : 0;
  const remaining = Math.max(0, total - at);

  return (
    <div className={`audionote ${compact ? 'compact' : ''}`}>
      <button
        type="button"
        className="audionote-play"
        onClick={toggle}
        aria-label={playing ? 'Pause' : 'Play voice note'}
      >
        <svg viewBox="0 0 24 24" width="17" height="17" fill="currentColor" aria-hidden="true">
          {playing
            ? <path d="M8 5h3v14H8zM13 5h3v14h-3z" />
            : <path d="M8 5.5v13a1 1 0 0 0 1.5.87l11-6.5a1 1 0 0 0 0-1.74l-11-6.5A1 1 0 0 0 8 5.5z" />}
        </svg>
      </button>

      <Waveform
        peaks={peaks}
        progress={progress}
        height={compact ? 26 : 34}
        onSeek={seek}
      />

      <span className="audionote-time">
        {clockLabel((playing || at > 0 ? remaining : total) * 1000)}
      </span>

      {failed && download && (
        // Nothing transcodes, so a clip recorded in one browser may not play
        // in another. Never leave it unreachable.
        <a className="audionote-fallback" href={src} download>Download</a>
      )}

      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setAt(0); }}
        onTimeUpdate={(event) => setAt(event.target.currentTime)}
        onLoadedMetadata={(event) => {
          // A recorder blob often reports Infinity until it is fully buffered,
          // so the duration measured at record time is the better number.
          if (Number.isFinite(event.target.duration)) setTotal(event.target.duration);
        }}
        onError={() => setFailed(true)}
      />
    </div>
  );
}
